// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

import type { Bundle, ClassRow } from '../data';
import { GRADE_ORDER } from '../data';
import { num, pct1, signed, safe, tipAttr, escapeHtml } from '../format';

/**
 * The second inversion. A diverging pyramid — men left, women right — with a
 * hollow outline of the anchor year sitting behind the current fill, so two
 * different structural changes read at once: the junior grades collapsing inside
 * their own 2006 shape while EL 1 bursts out of it, and the senior bars flipping
 * from left-heavy to right-heavy.
 *
 * Total is read from the workbook's own Total block and NEVER summed from Men +
 * Women, because employees who report a gender other than man or woman are
 * suppressed as a separate line but ARE included in the totals. Summing the two
 * visible bars under-reports the service by ~1,000 people.
 */

export function renderLadder(root: HTMLElement, b: Bundle): void {
  const years = b.classification.years;
  let anchor = years[0];
  let played = years[years.length - 1];
  let focus: string | null = null;

  root.innerHTML = `
    <div class="view-head">
      <h2>The shape of the service</h2>
      <p>Every APS employee by <span class="glossary-link" data-term="classification">classification</span>, men to the left and women to the right. The hollow outline is the same grade at the anchor year. Click a grade for its twenty-year history.</p>
    </div>
    <div class="controls">
      <div class="control-group">
        <span class="control-label">Anchor</span>
        <select id="ladder-anchor"></select>
      </div>
      <div class="control-group" style="flex:1 1 300px;min-width:0">
        <span class="control-label">Year</span>
        <input type="range" id="ladder-year" min="0" max="${years.length - 1}" value="${years.length - 1}" aria-label="Year" />
        <output id="ladder-out" class="num" style="font-weight:600;min-width:4ch"></output>
      </div>
    </div>
    <div class="callout" id="ladder-callout"></div>
    <div class="panel">
      <div class="chart-wrap" id="ladder-wrap"></div>
      <div class="legend">
        <span class="legend-item"><span class="legend-swatch" style="background:#2d86ab"></span> Men</span>
        <span class="legend-item"><span class="legend-swatch" style="background:#b26a00"></span> Women</span>
        <span class="legend-item"><span class="legend-swatch" style="background:transparent;border:1.5px solid var(--text-tertiary)"></span> The same grade at the anchor year</span>
        <span class="legend-item"><span class="glossary-link" data-term="genderX">Gender X</span> is in every total but in neither bar</span>
      </div>
    </div>
    <div id="ladder-detail"></div>`;

  const sel = root.querySelector<HTMLSelectElement>('#ladder-anchor')!;
  years.forEach((y) => {
    const o = document.createElement('option');
    o.value = String(y); o.textContent = String(y);
    if (y === anchor) o.selected = true;
    sel.appendChild(o);
  });

  const get = (rows: ClassRow[], grade: string, y: number): number | null => {
    const r = rows.find((x) => x.label === grade);
    if (!r) return null;
    const v = r.values[String(y)];
    return v === undefined ? null : v;
  };

  function draw(): void {
    const grades = GRADE_ORDER.filter((g) => b.classification.total.some((r) => r.label === g));
    const maxSide = Math.max(...grades.flatMap((g) => [
      get(b.classification.men, g, played) ?? 0, get(b.classification.women, g, played) ?? 0,
      get(b.classification.men, g, anchor) ?? 0, get(b.classification.women, g, anchor) ?? 0,
    ]), 1);

    const W = 900, rowH = 30, T = 30;
    const H = T + grades.length * rowH + 26;
    const mid = W / 2, half = mid - 74;
    const scale = (v: number) => (safe(v) / maxSide) * half;

    const bars = grades.map((g, i) => {
      const y = T + i * rowH;
      const m = get(b.classification.men, g, played) ?? 0;
      const w = get(b.classification.women, g, played) ?? 0;
      const mA = get(b.classification.men, g, anchor) ?? 0;
      const wA = get(b.classification.women, g, anchor) ?? 0;
      const tot = get(b.classification.total, g, played) ?? 0;
      const totA = get(b.classification.total, g, anchor) ?? 0;
      const bh = rowH - 9;
      const dim = focus && focus !== g;
      const tip = tipAttr([
        `${g} · ${played}`,
        `Men: ${num(m)}`,
        `Women: ${num(w)}`,
        `Total: ${num(tot)}`,
        `Women as a share of men + women: ${pct1(m + w ? (w / (m + w)) * 100 : null)}`,
        `Change vs ${anchor}: ${signed(tot - totA)}`,
        'Gender X is counted in Total but in neither bar.',
      ]);
      return `<g class="${dim ? 'dimmed' : ''}" data-grade="${escapeHtml(g)}" tabindex="0" role="button" data-tip="${escapeHtml(tip)}">
        <rect x="0" y="${y}" width="${W}" height="${bh}" fill="transparent" />
        <rect class="bar" x="${(mid - scale(m)).toFixed(1)}" y="${y}" width="${scale(m).toFixed(1)}" height="${bh}" fill="#2d86ab" />
        <rect class="bar" x="${mid}" y="${y}" width="${scale(w).toFixed(1)}" height="${bh}" fill="#b26a00" />
        <rect x="${(mid - scale(mA)).toFixed(1)}" y="${(y - 2).toFixed(1)}" width="${scale(mA).toFixed(1)}" height="${bh + 4}"
              fill="none" stroke="#5b7c99" stroke-width="1.2" stroke-dasharray="3 2" />
        <rect x="${mid}" y="${(y - 2).toFixed(1)}" width="${scale(wA).toFixed(1)}" height="${bh + 4}"
              fill="none" stroke="#5b7c99" stroke-width="1.2" stroke-dasharray="3 2" />
        <text x="${mid}" y="${y + bh / 2 + 4}" font-size="11" text-anchor="middle" font-weight="600"
              fill="var(--text-primary)" style="paint-order:stroke;stroke:#fff;stroke-width:3px">${escapeHtml(g)}</text>
        <text x="${(mid - half - 8).toFixed(1)}" y="${y + bh / 2 + 4}" font-size="10.5" text-anchor="end" class="num">${num(m)}</text>
        <text x="${(mid + half + 8).toFixed(1)}" y="${y + bh / 2 + 4}" font-size="10.5" text-anchor="start" class="num">${num(w)}</text>
      </g>`;
    }).join('');

    root.querySelector('#ladder-wrap')!.innerHTML = `
      <svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="APS employees by classification and gender, ${played}">
        <text x="${(mid - half / 2).toFixed(1)}" y="18" font-size="12" text-anchor="middle" font-weight="600" fill="#2d86ab">MEN</text>
        <text x="${(mid + half / 2).toFixed(1)}" y="18" font-size="12" text-anchor="middle" font-weight="600" fill="#b26a00">WOMEN</text>
        ${bars}
      </svg>`;
    (root.querySelector('#ladder-out') as HTMLOutputElement).textContent = String(played);

    const junior = ['APS 1', 'APS 2', 'APS 3'];
    const jNow = junior.reduce((s, g) => s + (get(b.classification.total, g, played) ?? 0), 0);
    const jThen = junior.reduce((s, g) => s + (get(b.classification.total, g, anchor) ?? 0), 0);
    const el1Now = get(b.classification.total, 'EL 1', played) ?? 0;
    const el1Then = get(b.classification.total, 'EL 1', anchor) ?? 0;
    const totNow = b.classification.totalsByYear[String(played)] ?? 0;
    const totThen = b.classification.totalsByYear[String(anchor)] ?? 0;
    root.querySelector('#ladder-callout')!.innerHTML =
      `Between ${anchor} and ${played} the APS grew from ${num(totThen)} to ${num(totNow)}. `
      + `Over the same period <strong>APS 1–3 fell from ${num(jThen)} to ${num(jNow)}</strong> `
      + `(${pct1((jThen ? jNow / jThen - 1 : 0) * 100)}), while <strong>EL 1 went from ${num(el1Then)} to ${num(el1Now)}</strong>. `
      + `EL 1 alone now outnumbers all of APS 1, 2 and 3 combined by ${jNow ? (el1Now / jNow).toFixed(2) : '—'} to 1, `
      + `having been outnumbered by them ${el1Then ? (jThen / el1Then).toFixed(2) : '—'} to 1 in ${anchor}.`;

    root.querySelector('#ladder-detail')!.innerHTML = focus ? detailFor(focus) : '';
  }

  function detailFor(grade: string): string {
    const W = 900, H = 220, L = 52, R = 16, T = 20, Bm = 30;
    const iw = W - L - R, ih = H - T - Bm;
    const m = years.map((y) => get(b.classification.men, grade, y) ?? 0);
    const w = years.map((y) => get(b.classification.women, grade, y) ?? 0);
    const max = Math.max(...m, ...w, 1);
    const xAt = (i: number) => L + (i / (years.length - 1)) * iw;
    const yAt = (v: number) => T + ih - (safe(v) / max) * ih;
    const line = (arr: number[]) => arr.map((v, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join('');
    const hot = years.map((y, i) => {
      const pctW = m[i] + w[i] ? (w[i] / (m[i] + w[i])) * 100 : null;
      return `<rect class="bar" x="${(xAt(i) - iw / (years.length * 2)).toFixed(1)}" y="${T}" width="${(iw / years.length).toFixed(1)}" height="${ih}" fill="transparent"
        data-tip="${escapeHtml(tipAttr([`${grade} · ${y}`, `Men: ${num(m[i])}`, `Women: ${num(w[i])}`, `Women's share: ${pct1(pctW)}`]))}"><title></title></rect>`;
    }).join('');
    return `<div class="panel">
      <div class="section-title">${escapeHtml(grade)}, ${years[0]}–${years[years.length - 1]}</div>
      <div class="section-sub">Men and women at this grade over twenty years. Click the grade again to close.</div>
      <div class="chart-wrap"><svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${escapeHtml(grade)} by gender over time">
        <line class="axis" x1="${L}" y1="${T + ih}" x2="${W - R}" y2="${T + ih}" />
        <path d="${line(m)}" fill="none" stroke="#2d86ab" stroke-width="2.2" />
        <path d="${line(w)}" fill="none" stroke="#b26a00" stroke-width="2.2" />
        <text x="${L}" y="${H - 10}" font-size="11">${years[0]}</text>
        <text x="${W - R}" y="${H - 10}" font-size="11" text-anchor="end">${years[years.length - 1]}</text>
        <text x="${L - 8}" y="${yAt(max) + 4}" font-size="11" text-anchor="end">${num(max)}</text>
        ${hot}
      </svg></div>
    </div>`;
  }

  sel.addEventListener('change', () => { anchor = Number(sel.value); draw(); });
  const scrub = root.querySelector<HTMLInputElement>('#ladder-year')!;
  scrub.addEventListener('input', () => { played = years[Number(scrub.value)]; draw(); });
  root.addEventListener('click', (e) => {
    const g = (e.target as Element).closest('[data-grade]');
    if (!g) return;
    const grade = g.getAttribute('data-grade')!;
    focus = focus === grade ? null : grade;
    draw();
  });

  draw();
}
